import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { UsersService } from '../../../../data-services/users.service';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { GenericNotificationID, ShoppingCartAbandonedNotificationID } from './constants';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class SendNotificationToTheUserService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly usersService: UsersService,
  ) {}

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const stepData = activity?.workflowStep ? JSON.parse(activity.workflowStep.data || '{}') : {};
    const userIds = activityData?.user_ids || [];
    const userId = userIds.length > 0 ? userIds[0] : null;
    const notificationId = stepData.notification_id;

    let name$ = of('');
    if (userId) {
      name$ = this.usersService.getUsersById(userIds).pipe(
        take(1),
        map((users) => (users && users.length > 0 ? users[0] : null)),
        map((user) => {
          if (!user) {
            return null;
          }
          return `${user.firstName} ${user.lastName}`;
        }),
      );
    }
    return name$.pipe(
      switchMap((name) => {
        if (name) {
          return this.translateService.stream(
            'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.ACTIVITY.TO_NAME',
            {
              name: name,
              notificationType: this.getNotificationTitle(notificationId),
            },
          );
        }
        if (!userId) {
          return this.translateService.stream(
            'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.ACTIVITY.DID_NOT_SEND',
          );
        }
        return this.translateService.stream(
          'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.ACTIVITY.SENT_A_NOTIFICATION',
          { notificationType: this.getNotificationTitle(notificationId) },
        );
      }),
    );
  }

  getTemplateActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    const notificationId = data.notification_id;
    if (data.user_ids?.length >= 1) {
      return this.usersService.getUsersById(data.user_ids).pipe(
        map((users) => {
          const name = this.usersService.getUserDisplay(users, users[0].userId);
          return this.translateService.instant(
            'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.ACTION.TO_USER',
            {
              name: name,
              notificationType: this.getNotificationTitle(notificationId),
            },
          );
        }),
      );
    }

    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    const notificationId = data.notification_id;
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.ACTION.WITHOUT_USER', {
      notificationType: this.getNotificationTitle(notificationId),
    });
  }

  getNotificationTitle(notificationId: string): string {
    let translationKey: string;
    switch (notificationId) {
      case GenericNotificationID:
        translationKey = 'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.NOTIFICATION_OPTIONS.GENERIC';
        break;
      case ShoppingCartAbandonedNotificationID:
        translationKey =
          'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.NOTIFICATION_OPTIONS.ABANDONED_SHOPPING_CART';
        break;
      default:
        translationKey = 'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.NOTIFICATION_OPTIONS.UNKNOWN';
    }
    return this.translateService.instant(translationKey);
  }
}
